import { useEffect, useRef } from "react";

// Makes a pop-up behave as a dialog for keyboard and screen-reader users
// (WCAG 2.1.2 No Keyboard Trap, 2.4.3 Focus Order, 4.1.2 Name, Role, Value):
// - on open, focus moves into the pop-up: its first text field if it has one,
//   otherwise the pop-up itself, so its title is read out first
// - Tab and Shift+Tab stay inside it
// - Escape closes it (when onClose is given)
// - on close, focus goes back to whatever opened it
// Attach the returned ref to the pop-up box and give that box
// role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}.

const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
  'select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

export default function useDialog(isOpen, onClose) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    const node = ref.current;
    if (!node) return undefined;
    const opener = document.activeElement;

    // A form pop-up starts in its first field (as the sign-in box always did);
    // any other pop-up focuses itself so its title is read first.
    if (!node.contains(document.activeElement)) {
      const field = node.querySelector(
        'input:not([disabled]):not([type="hidden"]):not([readonly]), select:not([disabled]), textarea:not([disabled])',
      );
      (field || node).focus();
    }

    const onKeyDown = (e) => {
      if (e.key === "Escape" && closeRef.current) {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const items = [...node.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.getClientRects().length > 0,
      );
      if (items.length === 0) {
        e.preventDefault();
        node.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === node || !node.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !node.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (opener && opener.focus && document.body.contains(opener)) {
        opener.focus();
      }
    };
  }, [isOpen]);

  return ref;
}
